import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import FocusedRowChip from '../common/FocusedRowChip';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import { ExpiryReportService } from '../../services/expiryReport.service';
import { ExpiryReport as Report, ExpiryReportFilter } from '../../interfaces/inventory/expiryReport.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import { fetchAllPages } from '../../services/salesOrder.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { saveBlob } from '../../utils/saveBlob';
import { formatMoney } from '../../utils/format';

interface Opt { id: string; name: string; }

const DEFAULT_WITHIN_DAYS = 30;

/** F6a lot expiry report: dated lots with stock left, expired and expiring within N days (Dhaka today). */
const ExpiryReport: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [searchParams] = useSearchParams();
  const [storeId, setStoreId] = useState(searchParams.get('storeId') ?? '');
  const [withinDays, setWithinDays] = useState(String(DEFAULT_WITHIN_DAYS));
  const [includeExpired, setIncludeExpired] = useState(true);
  const { focusId: focusLotId, clear: clearFocus } = useFocusedRow('lotId');
  const [stores, setStores] = useState<Opt[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  // The filters of the last RUN: the download exports what is on screen, not whatever was typed since.
  const [applied, setApplied] = useState<ExpiryReportFilter | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAllPages((p, size) => getStoresByInstitutionId(p, size, 'ALL'));
        setStores(s.map(x => ({ id: x.id!, name: x.storeName })));
      } catch (e) { toast.showApiError(e, 'Failed to load the store filter'); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const daysValid = /^\d+$/.test(withinDays) && Number(withinDays) <= 3650;

  const run = async () => {
    if (!daysValid) return;
    try {
      setLoading(true);
      const f: ExpiryReportFilter = { storeId: storeId || undefined, withinDays: Number(withinDays), includeExpired };
      setReport(await ExpiryReportService.report(institutionId!, f));
      setApplied(f);
    } catch (e) { toast.showApiError(e, 'Failed to load the lot expiry report'); } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    if (!applied) return;
    try {
      saveBlob(await ExpiryReportService.xlsx(institutionId!, applied), `lot-expiry-${report?.asOf ?? 'report'}.xlsx`);
    } catch (e) { toast.showApiError(e, 'Failed to download the Excel file'); }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="store.expiry.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Lot Expiry Report</Typography>
      <FocusedRowChip focusId={focusLotId} noun="lot" onClear={clearFocus} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.expiry.filter">
        <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
          <SearchableSelect<Opt> data-help="store.expiry.filter.store" options={stores} value={storeId} onChange={id => setStoreId(id)} getOptionLabel={o => o.name}
            label="Store" size="small" placeholder="All stores" />
        </Box>
        <TextField size="small" type="number" data-help="store.expiry.filter.days" label="Expiring within (days)" value={withinDays}
          inputProps={{ min: 0, max: 3650, 'data-testid': 'expiry-within-days' }}
          error={!daysValid} helperText={!daysValid ? '0 to 3650' : undefined}
          sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setWithinDays(e.target.value)} />
        <FormControlLabel sx={{ flex: '0 1 auto' }}
          control={<Checkbox checked={includeExpired} onChange={e => setIncludeExpired(e.target.checked)} />}
          data-help="store.expiry.filter.includeExpired" label="Include expired" />
        <Button data-help="store.expiry.run" variant="contained" onClick={run} disabled={loading || !daysValid}>Run</Button>
        <Button data-help="store.expiry.excel" startIcon={<FileDownloadIcon />} disabled={!report || !applied} onClick={download}>Excel</Button>
      </Box>
      {loading && <CircularProgress />}
      {report && !loading && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {report.includeExpired && (
              <Alert severity={report.expiredLots > 0 ? 'error' : 'success'} sx={{ flex: '1 1 260px' }} data-testid="expired-summary">
                Expired: {report.expiredLots} lot(s), {report.expiredQty} unit(s), {formatMoney(report.expiredValue)}
              </Alert>
            )}
            <Alert severity={report.expiringLots > 0 ? 'warning' : 'info'} sx={{ flex: '1 1 260px' }} data-testid="expiring-summary">
              Expiring within {report.withinDays} day(s): {report.expiringLots} lot(s), {report.expiringQty} unit(s), {formatMoney(report.expiringValue)}
            </Alert>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            As of {report.asOf} (Asia/Dhaka). Lots without an expiry date are not listed.
          </Typography>
          <TableContainer component={Paper} data-help-section="store.expiry.table">
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                  <TableCell data-help="store.expiry.col.store">Store</TableCell><TableCell data-help="store.expiry.col.item">Item</TableCell><TableCell data-help="store.expiry.col.lot">Lot</TableCell>
                  <TableCell data-help="store.expiry.col.received">Received</TableCell><TableCell data-help="store.expiry.col.manufactured">Manufactured</TableCell><TableCell data-help="store.expiry.col.expires">Expires</TableCell>
                  <TableCell data-help="store.expiry.col.daysLeft" align="right">Days left</TableCell><TableCell data-help="store.expiry.col.qty" align="right">Qty</TableCell>
                  <TableCell data-help="store.expiry.col.unitCost" align="right">Unit cost</TableCell><TableCell data-help="store.expiry.col.value" align="right">Value</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {report.rows.length === 0 && <TableRow><TableCell colSpan={10}>No dated lots in this window.</TableCell></TableRow>}
                  {(focusLotId ? report.rows.filter(r => r.lotId === focusLotId) : report.rows).map(r => (
                    <TableRow key={r.lotId}>
                      <TableCell>{r.storeName}</TableCell>
                      <TableCell>{r.itemCode} - {r.itemName}</TableCell>
                      <TableCell>#{r.lotNo}</TableCell>
                      <TableCell>{r.receiptDate ?? '-'}</TableCell>
                      <TableCell>{r.manufactureDate ?? '-'}</TableCell>
                      <TableCell>{r.expiryDate} {r.expired && <Chip size="small" color="error" label="Expired" />}</TableCell>
                      <TableCell align="right">{r.daysToExpiry}</TableCell>
                      <TableCell align="right">{r.remainingQty} {r.unit ?? ''}</TableCell>
                      <TableCell align="right">{formatMoney(r.unitCost)}</TableCell>
                      <TableCell align="right">{formatMoney(r.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ExpiryReport;
